import { Divider, useTheme } from "@mui/material";
import { useCallback, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import { RobotoStyledTab, RobotoStyledTabs } from "@/shared/components";
import { AIBadge } from "@/shared/components/ai";
import { useCheckFeatureFlag } from "@/shared/domain/featureFlags/hooks";

import { DatasetActionStatus } from "./DatasetActionStatus";
import { useActiveDatasetTab, DatasetTab } from "./useActiveDatasetTabFromUrl";

interface DatasetTabsProps {
  datasetId: string;
}

/**
 * Renders the dataset detail view's tab strip; the active tab is derived from
 * the URL by `useActiveDatasetTab`.
 *
 * Each tab link preserves the current `location.search` so query params
 * survive tab switches. The Files tab additionally preserves its last-visited
 * pathname (see `handleTabSwitch`) so deep directory navigation is restored
 * on return.
 */
export function DatasetTabs({ datasetId }: DatasetTabsProps) {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useActiveDatasetTab();
  const [filePath, setFilePath] = useState(DatasetTab.Files as string);
  const location = useLocation();
  const sessionsTabEnabled = useCheckFeatureFlag("release-sessions");
  // ``release-agents`` is the umbrella flag every new Agent feature
  // hides behind until the public launch. Co-located gates live in
  // ``shared/components/navigation/navigationLinks.ts`` (top-nav
  // entry), ``DatasetsRoutes.tsx`` (route admit), and
  // ``DatasetAgentThreads.tsx`` (component-level read). Sweep all
  // four sites together when the flag is retired.
  const agentThreadsTabEnabled = useCheckFeatureFlag("release-agents");

  const handleTabSwitch = useCallback(
    (currentTab: DatasetTab, nextTab: DatasetTab) => {
      // Files supports deep directory paths under its base route; capture
      // the current pathname on exit so the Files tab's `to` restores the
      // last-visited subdirectory on return.
      if (currentTab === DatasetTab.Files) {
        setFilePath(location.pathname);
      }

      setActiveTab(nextTab);
    },
    [location, setActiveTab, setFilePath],
  );

  return (
    <div
      style={{
        marginTop: theme.spacing(1),
      }}
    >
      <RobotoStyledTabs
        value={activeTab}
        onChange={(_event, nextTab: DatasetTab) => {
          handleTabSwitch(activeTab, nextTab);
        }}
      >
        <RobotoStyledTab
          value={DatasetTab.Files}
          label="Files"
          component={Link}
          to={{
            pathname: filePath,
            search: location.search,
          }}
          replace
        />
        {sessionsTabEnabled && (
          <RobotoStyledTab
            value={DatasetTab.Sessions}
            label="Sessions"
            component={Link}
            to={{
              pathname: DatasetTab.Sessions,
              search: location.search,
            }}
            replace
          />
        )}
        <RobotoStyledTab
          value={DatasetTab.Topics}
          label="Topics"
          component={Link}
          to={{
            pathname: DatasetTab.Topics,
            search: location.search,
          }}
          replace
        />
        <RobotoStyledTab
          value={DatasetTab.Events}
          label="Events"
          component={Link}
          to={{
            pathname: DatasetTab.Events,
            search: location.search,
          }}
          replace
        />
        <RobotoStyledTab
          icon={<DatasetActionStatus datasetId={datasetId} />}
          iconPosition="end"
          value={DatasetTab.Actions}
          label="Actions"
          component={Link}
          to={{
            pathname: DatasetTab.Actions,
            search: location.search,
          }}
          replace
        />
        {agentThreadsTabEnabled && (
          <RobotoStyledTab
            value={DatasetTab.AgentThreads}
            label="Agent Threads"
            component={Link}
            to={{
              pathname: DatasetTab.AgentThreads,
              search: location.search,
            }}
            replace
          />
        )}
        <RobotoStyledTab
          value={DatasetTab.Details}
          label="Details"
          component={Link}
          to={{
            pathname: DatasetTab.Details,
            search: location.search,
          }}
          replace
        />
        <RobotoStyledTab
          value={DatasetTab.Permissions}
          label="Permissions"
          component={Link}
          to={{
            pathname: DatasetTab.Permissions,
            search: location.search,
          }}
          replace
        />
        <RobotoStyledTab
          value={DatasetTab.AISummary}
          icon={<AIBadge />}
          iconPosition="start"
          label="&nbsp;&nbsp;AI"
          component={Link}
          to={{
            pathname: DatasetTab.AISummary,
            search: location.search,
          }}
          replace
        />
      </RobotoStyledTabs>
      <Divider
        sx={{
          borderColor: theme.border.color,
        }}
      />
    </div>
  );
}
